import {
  Box,
  Button,
  FileUpload,
  Flex,
  Separator,
  Text,
} from "@chakra-ui/react";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { FileListV2 } from "./FileListV2";
import { FileUploadStatus } from "../types";
import { useParty } from "@/_hooks/useParty";
import { LuCircleFadingArrowUp } from "react-icons/lu";
import {
  BIEvents,
  trackEvent,
} from "@/_components/lib/mixpanel/mixpanelClient";
import { useTranslations } from "next-intl";

interface FileUploadV2Props {
  title?: ReactNode;
  status?: FileUploadStatus;
  onFileAccept?: (data: FileUpload.FileAcceptDetails) => void;
  onFileReject?: (data: FileUpload.FileRejectDetails) => void;
  errorMessage?: string;
  fileAccepts?: {
    mimeType: string;
    displayName: string;
  }[];
  width?: string;
  maxFile?: number;
}

export const FileUploadV2 = ({
  title,
  status,
  onFileAccept,
  onFileReject,
  errorMessage,
  fileAccepts,
  width,
  maxFile = 1,
}: FileUploadV2Props) => {
  const t = useTranslations("space.talent_pool.file_upload");
  const colorByStatus = useMemo(() => {
    switch (status) {
      case "loading":
        return "primary.500";
      case "success":
        return "green.500";
      case "error":
        return "red.500";
      default:
        return "neutral";
    }
  }, [status]);

  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const party = useParty(ref);

  useEffect(() => {
    if (status === "success") {
      party.triggerParty();
    }
  }, [status]);

  const textByStatus = useMemo(() => {
    switch (status) {
      case "idle":
        return (
          <Box>
            {acceptedFiles.length === 0 ? (
              <Text textStyle="xs" color="neutral.700" fontWeight="semibold">
                <Text
                  as="span"
                  textStyle="xs"
                  color="neutral.700"
                  fontWeight="semibold"
                >
                  {t("choose_file")}
                </Text>{" "}
                {t("to_upload")}
              </Text>
            ) : (
              <Text textStyle="xs" color="neutral.700" fontWeight="semibold">
                {acceptedFiles?.[0]?.name || "FileName.pdf"}
              </Text>
            )}
          </Box>
        );
      case "loading":
        return (
          <Box textStyle="xs" color="neutral.700" fontWeight="semibold">
            {t("uploading_wait")}
          </Box>
        );
      case "success":
        return (
          <Box ref={ref}>
            <Text textStyle="xs" color="neutral.700" fontWeight="semibold">
              {acceptedFiles?.[0]?.name || "FileName.pdf"}
            </Text>
          </Box>
        );
      case "error":
        return (
          <Box>
            <Text textStyle="xs" color="red.400" fontWeight="semibold">
              {t("failed_to_upload")}{" "}
              {acceptedFiles?.[0]?.name || "FileName.pdf"}
            </Text>
            <Text textStyle="xs" color="red.500" mt={2}>
              {errorMessage || t("invalid_format")}
            </Text>
          </Box>
        );
      default:
        return null;
    }
  }, [status, acceptedFiles, errorMessage, t]);

  const onClickUpload = () => {
    trackEvent(BIEvents.TALENT_POOL_UPLOAD_FILE_DIALOG_OPEN, {
      action: "open",
    });
  };

  return (
    <FileUpload.Root
      maxW={width}
      alignItems="stretch"
      maxFiles={maxFile}
      accept={fileAccepts?.map((accept) => accept.mimeType)}
      maxFileSize={5 * 1024 * 1024}
      gap={0}
      onFileAccept={(data) => {
        onFileAccept?.(data);
        setAcceptedFiles(data.files);
      }}
      onFileReject={(data) => {
        onFileReject?.(data);
      }}
    >
      <FileUpload.HiddenInput />
      <Flex justify="flex-start" align="center" gap={2} pb={2} px={3}>
        <FileUpload.Trigger asChild>
          <Button
            colorPalette={colorByStatus}
            loading={status === "loading"}
            size="xs"
            variant="solid"
            onClick={onClickUpload}
          >
            <LuCircleFadingArrowUp /> {t("upload_file")}
          </Button>
        </FileUpload.Trigger>
        <Box textStyle="xs" color="neutral.700" fontWeight="semibold">
          {title || textByStatus}
          {status !== "error" && (
            <Box color="neutral.600">
              *
              {fileAccepts
                ?.map((accept) => `.${accept.displayName}`)
                .join(", ")}{" "}
              {t("up_to_size")}
            </Box>
          )}
        </Box>
      </Flex>

      <Separator />
      <FileListV2 />
    </FileUpload.Root>
  );
};
